import React, { useState, useEffect } from "react";
import "../../css/FileDragDrop.css";
import {
  FILE_DRAG_AND_DROP_PDF,
  FILE_DRAG_AND_DROP_TEXT,
  FILE_DRAG_AND_DROP_SIZE,
  VIEW_COPY_TYPE,
} from "../../redux/constants/metaData";
import { useCheckin } from "../../redux/hooks/useCheckin";
import { useTools } from "../../redux/hooks/useTools";
import { Stack } from "@fluentui/react";
import {
  getFileExtension,
  isValidPDFFileType,
} from "../../functions/helperMethod";
import { Label } from "@fluentui/react";
import "../../theme/css/viewCopyDragDropZone.css";
import DragAndDrop from "../../assets/icons/dragAndDrop.svg";
import { useDragAndDropStyle } from "../../theme/styles/DragAndDropStyle";
import { useTheme } from "../../theme/OAS365Theme";

const ViewCopyDragDropZone = ({
  isDisabled = false,
  boxHeight = "130px",
}) => {
  const { docFileTypeList, viewRequired, uploadFiles } = useCheckin();
  const { onViewCopyUploadChange } = useTools();
  const theme = useTheme();
  const gridThemeStyle = useDragAndDropStyle();
  const [dragActive, setDragActive] = React.useState(false);
  const inputRef = React.useRef(null);
  const [fileValidateMsg, setFileValidateMsg] = useState("");

  useEffect(() => {
    if (isDisabled) {
      setFileValidateMsg("");
    }
  }, [isDisabled]);

  const handleDrag = function (e) {
    if (isDisabled) return;
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = function (e) {
    if (isDisabled) return;
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      onDropCall(e.dataTransfer.files);
      e.target.value = null;
    }
  };

  const handleChange = function (e) {
    if (isDisabled) return;
    e.preventDefault();
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      const ext = getFileExtension(file.name);
      if (!ext || ext.toUpperCase() !== VIEW_COPY_TYPE) {
        setFileValidateMsg(
          ext ? `The ${ext.toUpperCase()} file type is not supported for View Copy. Only PDF files are allowed.` : "Invalid file type. Only PDF files are allowed."
        );
        e.target.value = null;
        return;
      }
      onDropCall(e.target.files);
      e.target.value = null;
    }
  };

  const onButtonClick = (e) => {
    if (isDisabled) return;
    e.preventDefault();
    e.stopPropagation();
    inputRef.current?.click();
  };

  function onDropCall(files) {
    const allFiles = [];
    for (var i = 0; i < 1; i++) {
      let file = files[i];
      let reader = new FileReader();
      reader.readAsDataURL(file);
      let fileSize = Math.round(file.size / 1024);
      fileSize = fileSize > 0 ? fileSize : 1;

      reader.onerror = () => {
        setFileValidateMsg("Failed to read the selected file. Please try again.");
      };

      reader.onload = () => {
        let fileInfo = {
          name: file.name,
          type: file.type,
          size: `${fileSize} KB`,
          base64: reader.result,
          base64str: reader.result.substring(
            reader.result.lastIndexOf(",") + 1
          ),
          file: file,
        };

        if (file.size > 1024 * 1024 * 20.5) {
          onViewCopyUploadChange(
            allFiles,
            allFiles.length,
            allFiles.length,
            viewRequired,
            uploadFiles
          );
          setFileValidateMsg(
            "File upload failed. The maximum file you can upload is 20 MB."
          );
        } else {
          const ext = getFileExtension(fileInfo.name);
          if (ext !== null && ext !== "") {
            const isValid = isValidPDFFileType(ext, VIEW_COPY_TYPE);
            if (isValid === true) {
              allFiles.push(fileInfo);
              onViewCopyUploadChange(
                allFiles,
                allFiles.length,
                allFiles.length,
                viewRequired,
                uploadFiles
              );
              setFileValidateMsg("");
            } else {
              onViewCopyUploadChange(
                allFiles,
                allFiles.length,
                allFiles.length,
                viewRequired,
                uploadFiles,
                true
              );
              setFileValidateMsg(
                `The ${ext.toUpperCase()} file type is not supported for View Copy`
              );
            }
          } else {
            onViewCopyUploadChange(
              allFiles,
              allFiles.length,
              allFiles.length,
              viewRequired,
              uploadFiles
            );
            setFileValidateMsg(`Invalid file type. Please try again.`);
          }
        }
      };
    }
  }

  return (
    <>
      <Stack className="container" style={{ width: "100%" }}>
        <Stack
          horizontalAlign="center"
          verticalAlign="center"
          id="div-file-upload"
          onDragEnter={handleDrag}
          onSubmit={(e) => e.preventDefault()}
          style={{ height: boxHeight }}
        >
          <input
            ref={inputRef}
            type="file"
            id="input-file-upload"
            multiple={true}
            accept="application/pdf,.pdf"
            onChange={handleChange}
            disabled={isDisabled}
          />
          <label
            style={{
              width: "100%",
              height: boxHeight,
              cursor: isDisabled ? "not-allowed" : "pointer",
              opacity: isDisabled ? 0.6 : 1,
              transition: "height 0.3s ease",
            }}
            id="label-file-upload"
            className={dragActive && !isDisabled ? "drag-active" : ""}
          >
            <Stack>
              <Stack.Item>
                <button
                  type="button"
                  className="upload-button"
                  onClick={onButtonClick}
                  disabled={isDisabled}
                  style={{
                    cursor: isDisabled ? "not-allowed" : "pointer",
                    opacity: isDisabled ? 0.6 : 1,
                  }}
                >
                  <Stack.Item>
                    <img
                      style={{
                        marginBottom: "8px",
                        verticalAlign: "middle",
                        filter: isDisabled ? "grayscale(100%)" : "none",
                      }}
                      src={DragAndDrop}
                      alt="drag and drop"
                    />
                  </Stack.Item>
                  <Stack.Item style={gridThemeStyle.uploadTextSpan}>
                    <span
                      style={{
                        ...gridThemeStyle.uploadTextHighlighted,
                        color: isDisabled
                          ? theme.colors.viewCopyUploadTextColor
                          : gridThemeStyle.uploadTextHighlighted.color,
                        fontWeight: "normal",
                      }}
                    >
                      {FILE_DRAG_AND_DROP_PDF}
                    </span>
                    <span
                      style={{
                        color: isDisabled
                          ? theme.colors.viewCopyUploadTextColor
                          : "inherit",
                        fontWeight: "normal",
                      }}
                    >
                      {FILE_DRAG_AND_DROP_TEXT}
                    </span>
                    <br />
                    <Stack.Item></Stack.Item>
                    <span
                      style={{
                        color: isDisabled
                          ? theme.colors.viewCopyUploadTextColor
                          : "inherit",
                        fontWeight: "normal",
                      }}
                    >
                      {FILE_DRAG_AND_DROP_SIZE}
                    </span>
                  </Stack.Item>
                </button>
              </Stack.Item>
            </Stack>
          </label>
          {dragActive && !isDisabled && (
            <div
              id="drag-file-element"
              onDragEnter={handleDrag}
              onDragLeave={handleDrag}
              onDragOver={handleDrag}
              onDrop={handleDrop}
            ></div>
          )}

          <Label className="labelStyles" style={gridThemeStyle.validationStyle}>
            {fileValidateMsg}
          </Label>
        </Stack>
      </Stack>
    </>
  );
};

export default ViewCopyDragDropZone;
